import { DetailPane } from '@/features/data-stream/components/DetailPane';
import { MessageListPane } from '@/features/data-stream/components/MessageListPane';
import { PaneResizer } from '@/features/data-stream/components/PaneResizer';
import { TopicTreePane } from '@/features/data-stream/components/TopicTreePane';
import { useMessageRate } from '@/features/data-stream/hooks/useMessageRate';
import type { TopicNode } from '@/features/data-stream/types';
import { type TopicStatInput, buildTopicTree } from '@/features/data-stream/utils/buildTopicTree';
import { classifyTopic } from '@/features/data-stream/utils/classifyTopic';
import { formatBytes } from '@/features/data-stream/utils/formatBytes';
import type { RawMqttMessage } from '@/lib/mqtt/types';
import { useDebugStore } from '@/lib/store/debug';
import type { Context } from '@/lib/store/ui';
import { useMemo } from 'react';
import { useDebugFeed } from './hooks/useDebugFeed';
import { useDebugPaneResize } from './hooks/useDebugResize';
import { useDebugScope } from './useDebugScope';

const LIVE_WINDOW_MS = 3_000;
const EMPTY: RawMqttMessage[] = [];

/** Filter a built tree to nodes whose leaves match the search string. */
function pruneBySearch(nodes: TopicNode[], q: string): TopicNode[] {
  if (!q) return nodes;
  const lower = q.toLowerCase();
  const walk = (node: TopicNode): TopicNode | null => {
    if (node.leaf) return node.path.toLowerCase().includes(lower) ? node : null;
    const kids = node.children.map(walk).filter((n): n is TopicNode => n !== null);
    return kids.length ? { ...node, children: kids } : null;
  };
  return nodes.map(walk).filter((n): n is TopicNode => n !== null);
}

/** Debug Logs panel — a Data-Stream-style Topics | Messages | JSON view, but
 *  scoped (via useDebugScope) to the topics the active tab is displaying. */
export function DebugPanel({ ctx }: { ctx: Context }) {
  const scope = useDebugScope(ctx);
  useDebugFeed(scope);

  const topics = useDebugStore((s) => s.topics);
  const buffers = useDebugStore((s) => s.buffers);
  const selectedTopic = useDebugStore((s) => s.selectedTopic);
  const selectedMsgId = useDebugStore((s) => s.selectedMsgId);
  const selectedMsg = useDebugStore((s) => s.selectedMsg);
  const detailTab = useDebugStore((s) => s.detailTab);
  const setTab = useDebugStore((s) => s.setTab);
  const search = useDebugStore((s) => s.search);
  const setSearch = useDebugStore((s) => s.setSearch);
  const paused = useDebugStore((s) => s.paused);
  const widths = useDebugStore((s) => s.paneWidths);
  const selectTopic = useDebugStore((s) => s.selectTopic);
  const selectMsg = useDebugStore((s) => s.selectMsg);

  const resizeTree = useDebugPaneResize('tree');
  const resizeMsgs = useDebugPaneResize('messages');

  const { tree, leafCount, liveCount } = useMemo(() => {
    const now = Date.now();
    const real = [...topics.values()];
    const realSet = new Set(real.map((t) => t.topic));
    const stats: TopicStatInput[] = real.map((t) => ({
      topic: t.topic,
      count: t.count,
      lastSeen: t.lastSeen,
    }));
    // Seed expected-but-silent topics (count 0) so the user can see a subscribed
    // topic that isn't arriving — e.g. surveillance_sensors/<id> for a
    // cooperative source. buildTopicTree marks count-0 nodes empty (dimmed).
    for (const t of scope.expected) {
      if (!realSet.has(t)) stats.push({ topic: t, count: 0, lastSeen: 0 });
    }
    const pruned = pruneBySearch(buildTopicTree(stats, LIVE_WINDOW_MS, now), search.trim());
    return { tree: pruned, leafCount: stats.length, liveCount: real.length };
  }, [topics, search, scope]);

  const messages = selectedTopic ? (buffers.get(selectedTopic) ?? EMPTY) : EMPTY;
  const selected = selectedMsg ?? null;
  const badge = selectedTopic ? classifyTopic(selectedTopic) : 'system';
  const rate = useMessageRate(messages);
  const stat = selectedTopic ? topics.get(selectedTopic) : undefined;
  const total = stat?.count ?? 0;
  const qos = stat?.lastQos ?? 0;
  const size = messages.length
    ? formatBytes(Math.round(messages.reduce((sum, m) => sum + m.bytes, 0) / messages.length))
    : '—';

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden">
      <div style={{ width: widths.tree }} className="shrink-0 border-r border-(--color-border)">
        <TopicTreePane
          tree={tree}
          leafCount={leafCount}
          liveCount={liveCount}
          search={search}
          onSearch={setSearch}
          selectedPath={selectedTopic}
          onSelect={selectTopic}
        />
      </div>
      <PaneResizer onMouseDown={resizeTree} />
      <div style={{ width: widths.messages }} className="shrink-0 border-r border-(--color-border)">
        <MessageListPane
          topic={selectedTopic}
          badge={badge}
          messages={messages}
          selectedId={selectedMsgId}
          onSelect={selectMsg}
          paused={paused}
          total={total}
          rate={rate}
          size={size}
          qos={qos}
        />
      </div>
      <PaneResizer onMouseDown={resizeMsgs} />
      <div className="min-w-0 flex-1">
        <DetailPane selected={selected} messages={messages} tab={detailTab} onTab={setTab} />
      </div>
    </div>
  );
}
